import { type FunctionComponent } from "react";
import Grid from "@mui/material/Grid";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import { useTranslation } from "react-i18next";
import { useFormikContext, getIn } from "formik";
import moment, { type Moment } from "moment-timezone";

import { type IncidentReportIndividualSupportSessionParticipant } from "components/CriticalIncident/interfaces";
import { FormControlWrapper } from "components/Shared";
import { TextBox, DateInput } from "components/Shared/Inputs";
import { ReportSectionAccordion } from "components/CriticalIncident/components/Shared/ReportSectionAccordion";
import { ChooseYesNo } from "components/CriticalIncident/components/ChooseYesNo";
import { ChooseYesNoOther } from "components/CriticalIncident/components/ChooseYesNoOther";
import { PriorityAlert } from "components/Shared/PriorityAlert";
import { EMAIL_MAX_LENGTH, PHONE_MAX_LENGTH } from "../../../RiskAssessments/RiskAssessments.constants";
import { EMPTY_PARTICIPANT, EMPTY_RISK_ASSESSMENT, MAX_CHARS } from "../../IndividualSupportSession.constants";
import { RiskAssessmentBlock } from "../RiskAssessmentBlock";
import { FollowUpSessions } from "../FollowUpSessions";

export interface ParticipantAccordionProps {
  index: number;
  participant: IncidentReportIndividualSupportSessionParticipant;
  isReadonly: boolean;
  isAU: boolean;
  removeAction?: () => void;
  removeHidden: boolean;
  patchParticipant: (partial: Partial<IncidentReportIndividualSupportSessionParticipant>) => void;
  expanded: boolean;
  onToggle: (isExpanded: boolean) => void;
}

export const ParticipantAccordion: FunctionComponent<ParticipantAccordionProps> = ({
  index,
  participant,
  isReadonly,
  isAU,
  removeAction,
  removeHidden,
  patchParticipant,
  expanded,
  onToggle,
}) => {
  const { t } = useTranslation("criticalIncident");
  const { touched, errors, setFieldValue, handleChange } = useFormikContext();
  const basePath = `individualSupportSession.participants[${index}]`;
  const participantTouched = getIn(touched, basePath) as Record<string, unknown> | undefined;
  const participantErrors = getIn(errors, basePath) as Record<string, string> | undefined;
  const participantDeclined = participant.declinedToProvideInformation;

  const updateField = (field: keyof IncidentReportIndividualSupportSessionParticipant, value: unknown) => {
    setFieldValue(`${basePath}.${field}`, value);
    patchParticipant({ [field]: value } as Partial<IncidentReportIndividualSupportSessionParticipant>);
  };

  const makeChangeHandler = (field: keyof IncidentReportIndividualSupportSessionParticipant) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      handleChange(event);
      updateField(field, event.target.value);
    };

  const handleBooleanChange = (field: keyof IncidentReportIndividualSupportSessionParticipant) => (value: boolean | string | null) => {
    if (field === "wasTheIndividualAtRisk" && value === true && isAU && !participant.riskAssessment) {
      setFieldValue(`${basePath}.riskAssessment`, { ...EMPTY_RISK_ASSESSMENT });
      patchParticipant({ wasTheIndividualAtRisk: true, riskAssessment: { ...EMPTY_RISK_ASSESSMENT } });
      setFieldValue(`${basePath}.wasTheIndividualAtRisk`, true);
      return;
    }

    // declining to provide information clears name and dob for that participant
    const updates: Partial<IncidentReportIndividualSupportSessionParticipant> = field === "declinedToProvideInformation" && value
      ? { [field]: value as boolean, name: "", dateOfBirth: null }
      : { [field]: value as boolean };

    Object.entries(updates).forEach(([updatedField, updatedValue]) => {
      setFieldValue(`${basePath}.${updatedField}`, updatedValue);
    });
    patchParticipant(updates);
  };

  const handleDateChange = (field: keyof IncidentReportIndividualSupportSessionParticipant) => (value: Moment | null) => {
    const isoString = value?.startOf("day")?.toDate().toISOString();
    setFieldValue(`${basePath}.${field}`, isoString);
    patchParticipant({ [field]: isoString || null } as Partial<IncidentReportIndividualSupportSessionParticipant>);
  };

  const patchRiskAssessment = (partial: Partial<IncidentReportIndividualSupportSessionParticipant["riskAssessment"]>) => {
    const nextRiskAssessment = { ...(participant.riskAssessment ?? EMPTY_RISK_ASSESSMENT), ...partial };
    patchParticipant({ riskAssessment: nextRiskAssessment });
  };

  return (
    <ReportSectionAccordion
      label={t("participant_number", { number: index + 1 })}
      name={basePath}
      pristineValue={EMPTY_PARTICIPANT}
      removeAction={isReadonly ? undefined : removeAction}
      expanded={expanded}
      onToggle={onToggle}
      readonly={isReadonly}
      removeHidden={removeHidden}
    >
      <Grid container spacing={2}>
        <Grid item sm={6}>
          <FormControlWrapper isReadonly={isReadonly} label={t("name")} name={`${basePath}.name`} fullWidth>
            <TextBox
              label={t("name")}
              name={`${basePath}.name`}
              value={participantDeclined ? "" : participant.name}
              onChange={makeChangeHandler("name")}
              slotProps={{ input: { maxLength: 200 } }}
              error={Boolean(participantTouched?.name) && Boolean(participantErrors?.name)}
              helperText={participantTouched?.name ? t(participantErrors?.name || " ") : ""}
              required={!participantDeclined}
              disabled={participantDeclined}
            />
          </FormControlWrapper>
        </Grid>
        <Grid item sm={6}>
          <FormControlWrapper
            isReadonly={isReadonly}
            type="date"
            dateFormat="L"
            label={t("date_of_birth")}
            name={`${basePath}.dateOfBirth`}
            required={!participantDeclined}
          >
            <DateInput
              disabled={participantDeclined}
              label={t("date_of_birth")}
              name={`${basePath}.dateOfBirth`}
              onChange={handleDateChange("dateOfBirth")}
              value={participantDeclined ? null : participant.dateOfBirth ? moment(participant.dateOfBirth) : null}
              error={Boolean(participantTouched?.dateOfBirth) && Boolean(participantErrors?.dateOfBirth)}
              helperText={participantTouched?.dateOfBirth ? t(participantErrors?.dateOfBirth || " ") : ""}
              fullWidth
            />
          </FormControlWrapper>
        </Grid>

        {isAU && (
          <>
            <Grid item sm={6}>
              <FormControlWrapper isReadonly={isReadonly} label={t("email")} name={`${basePath}.emailAddress`} fullWidth>
                <TextBox
                  label={t("email")}
                  name={`${basePath}.emailAddress`}
                  value={participantDeclined ? "" : participant.emailAddress}
                  onChange={makeChangeHandler("emailAddress")}
                  slotProps={{ input: { maxLength: EMAIL_MAX_LENGTH } }}
                  disabled={participantDeclined}
                />
              </FormControlWrapper>
            </Grid>
            <Grid item sm={6}>
              <FormControlWrapper isReadonly={isReadonly} label={t("phone")} name={`${basePath}.phoneNumber`} fullWidth>
                <TextBox
                  label={t("phone")}
                  name={`${basePath}.phoneNumber`}
                  value={participantDeclined ? "" : participant.phoneNumber}
                  onChange={makeChangeHandler("phoneNumber")}
                  slotProps={{ input: { maxLength: PHONE_MAX_LENGTH } }}
                  disabled={participantDeclined}
                />
              </FormControlWrapper>
            </Grid>
          </>
        )}

        <Grid item sm={12}>
          <FormControlWrapper
            isReadonly={isReadonly}
            type="radio.yesno"
            label={`${t("was_telus_health_support_discussed")}*`}
            name={`${basePath}.wasNatureOfSupportAndConfidentialityDiscussed`}
            fullWidth
            error={Boolean(participantTouched?.wasNatureOfSupportAndConfidentialityDiscussed) && Boolean(participantErrors?.wasNatureOfSupportAndConfidentialityDiscussed)}
            helperText={participantTouched?.wasNatureOfSupportAndConfidentialityDiscussed ? t(participantErrors?.wasNatureOfSupportAndConfidentialityDiscussed || " ") : ""}
          >
            <ChooseYesNo
              label={`${t("was_telus_health_support_discussed")}*`}
              name={`${basePath}.wasNatureOfSupportAndConfidentialityDiscussed`}
              value={participant.wasNatureOfSupportAndConfidentialityDiscussed}
              onChange={handleBooleanChange("wasNatureOfSupportAndConfidentialityDiscussed")}
            />
          </FormControlWrapper>
        </Grid>
        <Grid item sm={12}>
          <FormControlWrapper isReadonly={isReadonly} label={t("how_did_individual_present")} name={`${basePath}.howIndividualPresented`} fullWidth>
            <TextBox
              multiline
              minRows={4}
              maxRows={6}
              label={t("how_did_individual_present")}
              name={`${basePath}.howIndividualPresented`}
              value={participant.howIndividualPresented}
              onChange={makeChangeHandler("howIndividualPresented")}
              slotProps={{ input: { maxLength: MAX_CHARS } }}
            />
          </FormControlWrapper>
        </Grid>
        <Grid item sm={12}>
          <FormControlWrapper isReadonly={isReadonly} label={t("which_intervention_was_provided")} name={`${basePath}.interventionProvided`} fullWidth>
            <TextBox
              multiline
              minRows={4}
              maxRows={6}
              label={t("which_intervention_was_provided")}
              required
              name={`${basePath}.interventionProvided`}
              value={participant.interventionProvided}
              onChange={makeChangeHandler("interventionProvided")}
              slotProps={{ input: { maxLength: MAX_CHARS } }}
              error={Boolean(participantTouched?.interventionProvided) && Boolean(participantErrors?.interventionProvided)}
              helperText={participantTouched?.interventionProvided ? t(participantErrors?.interventionProvided || " ") : ""}
            />
          </FormControlWrapper>
        </Grid>
        <Grid item sm={12}>
          <FormControlWrapper isReadonly={isReadonly} label={t("followup_notes")} name={`${basePath}.followUpNotes`} fullWidth>
            <TextBox
              multiline
              minRows={4}
              maxRows={6}
              label={t("followup_notes")}
              name={`${basePath}.followUpNotes`}
              value={participant.followUpNotes}
              onChange={makeChangeHandler("followUpNotes")}
              slotProps={{ input: { maxLength: MAX_CHARS } }}
            />
          </FormControlWrapper>
        </Grid>
        <Grid item sm={12}>
          <FormControlWrapper
            isReadonly={isReadonly}
            name={`${basePath}.declinedToProvideInformation`}
            label={t("participant_declines_to_provide_information")}
            type="checkbox"
          >
            <FormControlLabel
              className="individual-support-session__decline-toggle"
              control={
                <Switch
                  checked={participant.declinedToProvideInformation}
                  onChange={(_e, checked) => handleBooleanChange("declinedToProvideInformation")(checked)}
                  name={`${basePath}.declinedToProvideInformation`}
                />
              }
              label={t("participant_declines_to_provide_information")}
            />
          </FormControlWrapper>
        </Grid>
        <Grid item sm={12}>
          <FormControlWrapper
            isReadonly={isReadonly}
            type="radio.yesno"
            label={`${t("was_individual_at_risk")}*`}
            name={`${basePath}.wasTheIndividualAtRisk`}
            fullWidth
            error={Boolean(participantTouched?.wasTheIndividualAtRisk) && Boolean(participantErrors?.wasTheIndividualAtRisk)}
            helperText={participantTouched?.wasTheIndividualAtRisk ? t(participantErrors?.wasTheIndividualAtRisk || " ") : ""}
          >
            <ChooseYesNo
              label={`${t("was_individual_at_risk")}*`}
              name={`${basePath}.wasTheIndividualAtRisk`}
              value={participant.wasTheIndividualAtRisk}
              onChange={handleBooleanChange("wasTheIndividualAtRisk")}
            />
          </FormControlWrapper>
        </Grid>

        {participant.wasTheIndividualAtRisk && (
          isAU ? (
            <RiskAssessmentBlock
              basePath={`${basePath}.riskAssessment`}
              riskAssessment={participant.riskAssessment ?? EMPTY_RISK_ASSESSMENT}
              patchRiskAssessment={patchRiskAssessment}
              isReadonly={isReadonly}
            />
          ) : (
            <Grid item sm={12}>
              {!isReadonly && (
                <PriorityAlert priority="normal">
                  {t("consultation_should_occur")}
                </PriorityAlert>
              )}
              <FormControlWrapper
                isReadonly={isReadonly}
                type="radio.yesnoother"
                label={`${t("was_there_supervision_or_consultation")}*`}
                name={`${basePath}.wasThereSupervisionOrConsultation`}
                fullWidth
                error={Boolean(participantTouched?.wasThereSupervisionOrConsultation) && Boolean(participantErrors?.wasThereSupervisionOrConsultation)}
                helperText={participantTouched?.wasThereSupervisionOrConsultation ? t(participantErrors?.wasThereSupervisionOrConsultation || " ") : ""}
              >
                <ChooseYesNoOther
                  label={t("was_there_supervision_or_consultation")}
                  name={`${basePath}.wasThereSupervisionOrConsultation`}
                  value={participant.wasThereSupervisionOrConsultation}
                  onChange={handleBooleanChange("wasThereSupervisionOrConsultation")}
                  required
                />
              </FormControlWrapper>
              {(participant.wasThereSupervisionOrConsultation === "Y" || participant.wasThereSupervisionOrConsultation === "O") && (
                <FormControlWrapper
                  isReadonly={isReadonly}
                  label={t("supervision_or_consultation_other_reason")}
                  name={`${basePath}.wasThereSupervisionOrConsultationOtherReason`}
                  fullWidth
                >
                  <TextBox
                    multiline
                    minRows={4}
                    maxRows={6}
                    label={t("supervision_or_consultation_other_reason")}
                    name={`${basePath}.wasThereSupervisionOrConsultationOtherReason`}
                    value={participant.wasThereSupervisionOrConsultationOtherReason}
                    onChange={makeChangeHandler("wasThereSupervisionOrConsultationOtherReason")}
                    slotProps={{ input: { maxLength: MAX_CHARS } }}
                    required
                    error={Boolean(participantTouched?.wasThereSupervisionOrConsultationOtherReason) && Boolean(participantErrors?.wasThereSupervisionOrConsultationOtherReason)}
                    helperText={participantTouched?.wasThereSupervisionOrConsultationOtherReason ? t(participantErrors?.wasThereSupervisionOrConsultationOtherReason || " ") : ""}
                  />
                </FormControlWrapper>
              )}
              {["Y", "O"].includes(participant.wasThereSupervisionOrConsultation) && (
                <>
                  <FormControlWrapper
                    isReadonly={isReadonly}
                    type="date"
                    dateFormat="L"
                    label={t("supervision_date")}
                    name={`${basePath}.supervisionDate`}
                    required={participant.wasThereSupervisionOrConsultation === "Y"}
                  >
                    <DateInput
                      label={t("supervision_date")}
                      name={`${basePath}.supervisionDate`}
                      onChange={handleDateChange("supervisionDate")}
                      value={participant.supervisionDate ? moment(participant.supervisionDate) : null}
                      error={Boolean(participantTouched?.supervisionDate) && Boolean(participantErrors?.supervisionDate)}
                      helperText={participantTouched?.supervisionDate ? t(participantErrors?.supervisionDate || " ") : ""}
                      fullWidth
                    />
                  </FormControlWrapper>
                  <FormControlWrapper
                    isReadonly={isReadonly}
                    label={t("supervision_or_consultation_outcome")}
                    name={`${basePath}.supervisionOrConsultationOutcome`}
                    fullWidth
                  >
                    <TextBox
                      multiline
                      minRows={4}
                      maxRows={6}
                      label={t("supervision_or_consultation_outcome")}
                      name={`${basePath}.supervisionOrConsultationOutcome`}
                      value={participant.supervisionOrConsultationOutcome}
                      onChange={makeChangeHandler("supervisionOrConsultationOutcome")}
                      slotProps={{ input: { maxLength: MAX_CHARS } }}
                      required={participant.wasThereSupervisionOrConsultation === "Y"}
                      error={Boolean(participantTouched?.supervisionOrConsultationOutcome) && Boolean(participantErrors?.supervisionOrConsultationOutcome)}
                      helperText={participantTouched?.supervisionOrConsultationOutcome ? t(participantErrors?.supervisionOrConsultationOutcome || " ") : ""}
                    />
                  </FormControlWrapper>
                </>
              )}
            </Grid>
          )
        )}

        {isAU && (
          <FollowUpSessions
            basePath={basePath}
            participant={participant}
            isReadonly={isReadonly}
            patchParticipant={patchParticipant}
          />
        )}
      </Grid>
    </ReportSectionAccordion>
  );
};
